import {OfferRuleModel} from '../../../../../model/offer-rule.model';
import {OfferRuleTypeEnumerator} from '../enumerators/offer-rule-type.enumerator';
import {EnhancedRuleDraft, EnhancedRuleServerMetadata} from '../models/enhanced-rule-draft.model';
import {
  GeneratedAprOfferDraft,
  GeneratedFinanceOfferDraft,
  GeneratedLeaseOfferDraft,
  GeneratedOfferSetDraft,
  MultiVinVisibilityScope
} from '../models/generated-offer-set-draft.model';
import {
  offerRuleWorkflowSchemaVersion,
  OfferRuleWorkflowAcceptance,
  OfferRuleWorkflowAggregateCollection,
  OfferRuleWorkflowAggregateItem,
  OfferRuleWorkflowCommandStatusResponse,
  OfferRuleWorkflowCreateOperation,
  OfferRuleWorkflowDeleteOperation,
  OfferRuleWorkflowFinanceData,
  OfferRuleWorkflowLeaseData,
  OfferRuleWorkflowOffersData,
  OfferRuleWorkflowPromotionalAprData,
  OfferRuleWorkflowPrivacy,
  OfferRuleWorkflowRuleData,
  OfferRuleWorkflowUpdateOperation,
  OfferRuleWorkflowVisibility
} from '../models/offer-rule-workflow-api.model';
import {
  QualifyingRuleSourceAprOffer,
  QualifyingRuleSourceDetail,
  QualifyingRuleSourceFinanceOffer,
  QualifyingRuleSourceLeaseOffer
} from '../models/qualifying-rule-source-detail.model';
import {QualifyingRuleSourceRow} from '../models/qualifying-rule-source.model';

const defaultIsCustom = false;
const defaultUseDefaultSettings = true;
const decimalPattern = /^(0|[1-9]\d*)\.\d{2}$/;
const requestIdPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const maximumCurrency = '99999999.99';
const maximumPercentage = '999.99';
const maximumTextLength = 255;
const offerSetType = 'offer_set';
const dollarDiscountType = 'dollar_discount';

type EnhancedRuleDraftNormalizer = (draft: EnhancedRuleDraft) => EnhancedRuleDraft;

export function mapSourceToGeneratedOfferSetDraft(source: QualifyingRuleSourceDetail): GeneratedOfferSetDraft
{
  const generatedOfferSet = {
    multiVinVisibility: MultiVinVisibilityScope.FullVisibility,
    lease: mapLeaseOffer(source.offers.lease),
    alternateLease: mapLeaseOffer(source.offers.alternateLease),
    finance: mapFinanceOffer(source.offers.finance),
    promotionalApr: mapAprOffer(source.offers.promotionalApr)
  };

  return generatedOfferSet;
}

export function cloneOfferRule(rule: OfferRuleModel): OfferRuleModel
{
  const clone: OfferRuleModel = {
    id: rule.id,
    accountId: rule.accountId,
    offerSetId: rule.offerSetId,
    year: rule.year,
    make: rule.make,
    model: rule.model,
    trim: rule.trim,
    maxSalePrice: rule.maxSalePrice,
    vehicleLimit: rule.vehicleLimit,
    vehicleCondition: rule.vehicleCondition,
    dollarAmountDiscount: rule.dollarAmountDiscount,
    isPrivate: rule.isPrivate,
    status: rule.status,
    created: rule.created,
    modified: rule.modified
  };

  return clone;
}

export function cloneQualifyingRuleSource(source: QualifyingRuleSourceRow): QualifyingRuleSourceRow
{
  const clone = {...source};

  return clone;
}

export function cloneGeneratedOfferSetDraft(generatedOfferSet: GeneratedOfferSetDraft): GeneratedOfferSetDraft
{
  const clone = {
    multiVinVisibility: generatedOfferSet.multiVinVisibility ?? MultiVinVisibilityScope.FullVisibility,
    lease: cloneLeaseOfferDraft(generatedOfferSet.lease),
    alternateLease: cloneLeaseOfferDraft(generatedOfferSet.alternateLease),
    finance: cloneFinanceOfferDraft(generatedOfferSet.finance),
    promotionalApr: cloneAprOfferDraft(generatedOfferSet.promotionalApr)
  };

  return clone;
}

export function cloneEnhancedRuleDraft(draft: EnhancedRuleDraft): EnhancedRuleDraft
{
  const clone = {
    ruleType: draft.ruleType,
    rule: cloneOfferRule(draft.rule),
    source: cloneOptionalQualifyingRuleSource(draft.source),
    generatedOfferSet: cloneOptionalGeneratedOfferSetDraft(draft.generatedOfferSet),
    serverMetadata: cloneOptionalServerMetadata(draft.serverMetadata)
  };

  return clone;
}

function normalizeOfferSetRuleDraft(draft: EnhancedRuleDraft): EnhancedRuleDraft
{
  const normalizedDraft = cloneEnhancedRuleDraft(draft);

  return normalizedDraft;
}

export function normalizeDollarDiscountRuleDraft(draft: EnhancedRuleDraft): EnhancedRuleDraft
{
  const normalizedDraft = cloneEnhancedRuleDraft(draft);
  normalizedDraft.ruleType = OfferRuleTypeEnumerator.DollarDiscountRule;
  normalizedDraft.rule.isPrivate = true;
  delete normalizedDraft.rule.offerSetId;
  normalizedDraft.source = null;
  normalizedDraft.generatedOfferSet = null;

  return normalizedDraft;
}

const enhancedRuleDraftNormalizers: Record<OfferRuleTypeEnumerator, EnhancedRuleDraftNormalizer> = {
  [OfferRuleTypeEnumerator.OfferSetRule]: normalizeOfferSetRuleDraft,
  [OfferRuleTypeEnumerator.DollarDiscountRule]: normalizeDollarDiscountRuleDraft
};

export function normalizeEnhancedRuleDraft(draft: EnhancedRuleDraft): EnhancedRuleDraft
{
  const normalizer = enhancedRuleDraftNormalizers[draft.ruleType];
  const normalizedDraft = normalizer(draft);

  return normalizedDraft;
}

export function selectOfferRuleForPersistence(draft: EnhancedRuleDraft): OfferRuleModel
{
  const normalizedDraft = normalizeEnhancedRuleDraft(draft);
  const rule = cloneOfferRule(normalizedDraft.rule);
  if (normalizedDraft.rule.offerSetId === undefined) {
    delete rule.offerSetId;
  }

  return rule;
}

function mapLeaseOffer(source: QualifyingRuleSourceLeaseOffer | null): GeneratedLeaseOfferDraft
{
  const draft = createBlankLeaseOfferDraft();
  if (source !== null) {
    draft.paymentAmount = normalizeOptionalSourceDecimal(source.paymentAmount);
    draft.moneyDown = normalizeOptionalSourceDecimal(source.moneyDown);
    draft.percentageDown = normalizeOptionalSourceDecimal(source.percentageDown);
    draft.term = source.term;
    draft.annualMileage = source.annualMileage;
    draft.additionalText = source.additionalText;
  }

  return draft;
}

function createBlankLeaseOfferDraft(): GeneratedLeaseOfferDraft
{
  const draft = {
    paymentAmount: null,
    moneyDown: null,
    percentageDown: null,
    term: null,
    annualMileage: null,
    additionalText: null,
    isCustom: defaultIsCustom
  };

  return draft;
}

function mapFinanceOffer(source: QualifyingRuleSourceFinanceOffer | null): GeneratedFinanceOfferDraft
{
  const draft = createBlankFinanceOfferDraft();
  if (source !== null) {
    draft.paymentAmount = normalizeOptionalSourceDecimal(source.paymentAmount);
    draft.moneyDown = normalizeOptionalSourceDecimal(source.moneyDown);
    draft.percentageDown = normalizeOptionalSourceDecimal(source.percentageDown);
    draft.apr = normalizeOptionalSourceDecimal(source.apr);
    draft.term = source.term;
    draft.additionalText = source.additionalText;
  }

  return draft;
}

function createBlankFinanceOfferDraft(): GeneratedFinanceOfferDraft
{
  const draft = {
    paymentAmount: null,
    moneyDown: null,
    percentageDown: null,
    apr: null,
    term: null,
    additionalText: null,
    isCustom: defaultIsCustom
  };

  return draft;
}

function mapAprOffer(source: QualifyingRuleSourceAprOffer | null): GeneratedAprOfferDraft
{
  const draft = createBlankAprOfferDraft();
  if (source !== null) {
    draft.apr = normalizeOptionalSourceDecimal(source.apr);
    draft.term = source.term;
    draft.additionalText = source.additionalText;
  }

  return draft;
}

function createBlankAprOfferDraft(): GeneratedAprOfferDraft
{
  const draft = {
    apr: null,
    term: null,
    additionalText: null,
    useDefaultSettings: defaultUseDefaultSettings
  };

  return draft;
}

function cloneLeaseOfferDraft(draft: GeneratedLeaseOfferDraft): GeneratedLeaseOfferDraft
{
  const clone = {...draft};

  return clone;
}

function cloneFinanceOfferDraft(draft: GeneratedFinanceOfferDraft): GeneratedFinanceOfferDraft
{
  const clone = {...draft};

  return clone;
}

function cloneAprOfferDraft(draft: GeneratedAprOfferDraft): GeneratedAprOfferDraft
{
  const clone = {...draft};

  return clone;
}

function cloneOptionalQualifyingRuleSource(source: QualifyingRuleSourceRow | null): QualifyingRuleSourceRow | null
{
  let clone: QualifyingRuleSourceRow | null = null;
  if (source !== null) {
    clone = cloneQualifyingRuleSource(source);
  }

  return clone;
}

function cloneOptionalGeneratedOfferSetDraft(generatedOfferSet: GeneratedOfferSetDraft | null): GeneratedOfferSetDraft | null
{
  let clone: GeneratedOfferSetDraft | null = null;
  if (generatedOfferSet !== null) {
    clone = cloneGeneratedOfferSetDraft(generatedOfferSet);
  }

  return clone;
}

function cloneOptionalServerMetadata(metadata: EnhancedRuleServerMetadata | null): EnhancedRuleServerMetadata | null
{
  let clone: EnhancedRuleServerMetadata | null = null;
  if (metadata !== null) {
    clone = {
      ruleId: metadata.ruleId,
      version: metadata.version,
      generatedOfferSetId: metadata.generatedOfferSetId
    };
  }

  return clone;
}

function normalizeOptionalSourceDecimal(value: number | null): string | null
{
  let normalizedValue: string | null = null;
  if (value !== null && Number.isFinite(value) && value >= 0) {
    normalizedValue = value.toFixed(2);
  }

  return normalizedValue;
}

export function mapAggregateItemToEnhancedRuleDraft(
  item: OfferRuleWorkflowAggregateItem,
  accountId: number
): EnhancedRuleDraft
{
  const rule = mapAggregateRule(item, accountId);
  let generatedOfferSet: GeneratedOfferSetDraft | null = null;
  let generatedOfferSetId: number | null = null;
  let ruleType = OfferRuleTypeEnumerator.DollarDiscountRule;
  if (item.type === offerSetType) {
    ruleType = OfferRuleTypeEnumerator.OfferSetRule;
    generatedOfferSet = mapPersistedOfferSet(item.offerSet.offers, item.offerSet.visibilityScope);
    generatedOfferSetId = item.offerSet.generatedOfferSetId;
  }
  const draft: EnhancedRuleDraft = {
    ruleType,
    rule,
    source: null,
    generatedOfferSet,
    serverMetadata: {ruleId: item.ruleId, version: item.version, generatedOfferSetId}
  };

  return draft;
}

export function mapDraftToCreateOperation(
  clientKey: string,
  draft: EnhancedRuleDraft
): OfferRuleWorkflowCreateOperation
{
  let operation: OfferRuleWorkflowCreateOperation;
  const rule = mapDraftRuleData(draft);
  if (draft.ruleType === OfferRuleTypeEnumerator.OfferSetRule) {
    const sourceOfferSetId = requirePositiveSafeInteger(draft.source?.offerSetId, 'sourceOfferSetId');
    const generated = requireGeneratedDraft(draft);
    operation = {
      clientKey,
      type: offerSetType,
      rule,
      offerSet: {
        sourceOfferSetId,
        visibilityScope: generated.multiVinVisibility,
        offers: mapGeneratedOffers(generated)
      }
    };
  } else {
    operation = {clientKey, type: dollarDiscountType, rule, offerSet: null};
  }

  return operation;
}

export function mapDraftToUpdateOperation(draft: EnhancedRuleDraft): OfferRuleWorkflowUpdateOperation
{
  const metadata = requireServerMetadata(draft);
  const rule = mapDraftRuleData(draft);
  let operation: OfferRuleWorkflowUpdateOperation;
  if (draft.ruleType === OfferRuleTypeEnumerator.OfferSetRule) {
    const generated = requireGeneratedDraft(draft);
    operation = {
      ruleId: metadata.ruleId,
      version: metadata.version,
      type: offerSetType,
      rule,
      offerSet: {
        visibilityScope: generated.multiVinVisibility,
        offers: mapGeneratedOffers(generated)
      }
    };
  } else {
    operation = {
      ruleId: metadata.ruleId,
      version: metadata.version,
      type: dollarDiscountType,
      rule,
      offerSet: null
    };
  }

  return operation;
}

export function mapDraftToDeleteOperation(draft: EnhancedRuleDraft): OfferRuleWorkflowDeleteOperation
{
  const metadata = requireServerMetadata(draft);
  const operation = {ruleId: metadata.ruleId, version: metadata.version};

  return operation;
}

export function areEnhancedRuleDraftsEqual(first: EnhancedRuleDraft, second: EnhancedRuleDraft): boolean
{
  const firstValue = buildComparableDraft(first);
  const secondValue = buildComparableDraft(second);
  const equal = JSON.stringify(firstValue) === JSON.stringify(secondValue);

  return equal;
}

function mapAggregateRule(item: OfferRuleWorkflowAggregateItem, accountId: number): OfferRuleModel
{
  const rule = new OfferRuleModel();
  rule.id = item.ruleId;
  rule.accountId = accountId;
  if (item.type === offerSetType) {
    rule.offerSetId = item.offerSet.generatedOfferSetId;
  }
  rule.year = item.rule.year ?? undefined;
  rule.make = item.rule.make ?? undefined;
  rule.model = item.rule.model ?? undefined;
  rule.trim = item.rule.trim ?? undefined;
  rule.maxSalePrice = Number(item.rule.maxSalePrice);
  rule.vehicleLimit = item.rule.vehicleLimit ?? undefined;
  rule.vehicleCondition = item.rule.vehicleCondition;
  rule.dollarAmountDiscount = undefined;
  if (item.rule.dollarAmountDiscount !== undefined) {
    rule.dollarAmountDiscount = Number(item.rule.dollarAmountDiscount);
  }
  rule.status = item.rule.status;
  rule.isPrivate = item.rule.privacy === 'private';
  rule.created = item.created ?? undefined;
  rule.modified = item.modified ?? undefined;

  return rule;
}

function mapPersistedOfferSet(
  offers: OfferRuleWorkflowOffersData,
  visibility: OfferRuleWorkflowVisibility
): GeneratedOfferSetDraft
{
  let multiVinVisibility = MultiVinVisibilityScope.FullVisibility;
  if (visibility === MultiVinVisibilityScope.InventoryOnly) {
    multiVinVisibility = MultiVinVisibilityScope.InventoryOnly;
  }
  const generated: GeneratedOfferSetDraft = {
    multiVinVisibility,
    lease: mapPersistedLease(offers.lease),
    alternateLease: mapPersistedLease(offers.alternateLease),
    finance: mapPersistedFinance(offers.finance),
    promotionalApr: mapPersistedApr(offers.promotionalApr)
  };

  return generated;
}

function mapPersistedLease(lease: OfferRuleWorkflowLeaseData): GeneratedLeaseOfferDraft
{
  const draft: GeneratedLeaseOfferDraft = {
    paymentAmount: lease.paymentAmount,
    moneyDown: lease.moneyDown,
    percentageDown: lease.percentageDown,
    term: lease.term,
    annualMileage: lease.annualMileage,
    additionalText: lease.additionalText,
    isCustom: lease.mode === 'custom'
  };

  return draft;
}

function mapPersistedFinance(finance: OfferRuleWorkflowFinanceData): GeneratedFinanceOfferDraft
{
  const draft: GeneratedFinanceOfferDraft = {
    paymentAmount: finance.paymentAmount,
    moneyDown: finance.moneyDown,
    percentageDown: finance.percentageDown,
    apr: finance.apr,
    term: finance.term,
    additionalText: finance.additionalText,
    isCustom: finance.mode === 'custom'
  };

  return draft;
}

function mapPersistedApr(apr: OfferRuleWorkflowPromotionalAprData): GeneratedAprOfferDraft
{
  const draft: GeneratedAprOfferDraft = {
    apr: apr.apr,
    term: apr.term,
    additionalText: apr.additionalText,
    useDefaultSettings: apr.mode === 'default'
  };

  return draft;
}

function mapDraftRuleData(draft: EnhancedRuleDraft): OfferRuleWorkflowRuleData
{
  const condition = normalizeVehicleCondition(draft.rule.vehicleCondition);
  const status = normalizeRuleStatus(draft.rule.status);
  let privacy: OfferRuleWorkflowPrivacy = 'public';
  let dollarAmountDiscount: string | undefined;
  if (draft.rule.isPrivate) {
    privacy = 'private';
  }
  if (draft.ruleType === OfferRuleTypeEnumerator.DollarDiscountRule) {
    dollarAmountDiscount = normalizeRequiredDecimal(
      draft.rule.dollarAmountDiscount,
      maximumCurrency,
      true
    );
  }
  const rule: OfferRuleWorkflowRuleData = {
    year: normalizeOptionalSafeInteger(draft.rule.year, true, 'year'),
    make: normalizeOptionalText(draft.rule.make),
    model: normalizeOptionalText(draft.rule.model),
    trim: normalizeOptionalText(draft.rule.trim),
    maxSalePrice: normalizeRequiredDecimal(draft.rule.maxSalePrice, maximumCurrency, false),
    vehicleLimit: normalizeOptionalSafeInteger(draft.rule.vehicleLimit, false, 'vehicleLimit'),
    vehicleCondition: condition,
    dollarAmountDiscount,
    status,
    privacy
  };

  return rule;
}

function mapGeneratedOffers(generated: GeneratedOfferSetDraft): OfferRuleWorkflowOffersData
{
  const offers: OfferRuleWorkflowOffersData = {
    lease: mapGeneratedLease(generated.lease),
    alternateLease: mapGeneratedLease(generated.alternateLease),
    finance: mapGeneratedFinance(generated.finance),
    promotionalApr: mapGeneratedApr(generated.promotionalApr)
  };

  return offers;
}

function mapGeneratedLease(draft: GeneratedLeaseOfferDraft): OfferRuleWorkflowLeaseData
{
  let mode: 'custom' | 'default' = 'default';
  if (draft.isCustom) {
    mode = 'custom';
  }
  const lease: OfferRuleWorkflowLeaseData = {
    mode,
    paymentAmount: normalizeOptionalDecimal(draft.paymentAmount, maximumCurrency),
    moneyDown: normalizeOptionalDecimal(draft.moneyDown, maximumCurrency),
    percentageDown: normalizeOptionalDecimal(draft.percentageDown, maximumPercentage),
    term: normalizeOptionalSafeInteger(draft.term, true, 'term'),
    annualMileage: normalizeOptionalSafeInteger(draft.annualMileage, false, 'annualMileage'),
    additionalText: normalizeOptionalText(draft.additionalText)
  };
  validateCustomLease(lease);

  return lease;
}

function mapGeneratedFinance(draft: GeneratedFinanceOfferDraft): OfferRuleWorkflowFinanceData
{
  let mode: 'custom' | 'default' = 'default';
  if (draft.isCustom) {
    mode = 'custom';
  }
  const finance: OfferRuleWorkflowFinanceData = {
    mode,
    paymentAmount: normalizeOptionalDecimal(draft.paymentAmount, maximumCurrency),
    moneyDown: normalizeOptionalDecimal(draft.moneyDown, maximumCurrency),
    percentageDown: normalizeOptionalDecimal(draft.percentageDown, maximumPercentage),
    apr: normalizeOptionalDecimal(draft.apr, maximumPercentage),
    term: normalizeOptionalSafeInteger(draft.term, true, 'term'),
    additionalText: normalizeOptionalText(draft.additionalText)
  };
  validateCustomFinance(finance);

  return finance;
}

function mapGeneratedApr(draft: GeneratedAprOfferDraft): OfferRuleWorkflowPromotionalAprData
{
  let mode: 'custom' | 'default' = 'custom';
  if (draft.useDefaultSettings) {
    mode = 'default';
  }
  const apr: OfferRuleWorkflowPromotionalAprData = {
    mode,
    apr: normalizeOptionalDecimal(draft.apr, maximumPercentage),
    term: normalizeOptionalSafeInteger(draft.term, true, 'term'),
    additionalText: normalizeOptionalText(draft.additionalText)
  };
  if (mode === 'custom' && (apr.apr === null || apr.term === null)) {
    throw new RangeError('Custom Promotional APR values are incomplete.');
  }

  return apr;
}

function validateCustomLease(lease: OfferRuleWorkflowLeaseData): void
{
  const activeDownValues = Number(lease.moneyDown !== null) + Number(lease.percentageDown !== null);
  if (lease.mode === 'custom'
    && (lease.paymentAmount === null || lease.term === null || lease.annualMileage === null || activeDownValues !== 1)
  ) {
    throw new RangeError('Custom lease values are incomplete or contradictory.');
  }
}

function validateCustomFinance(finance: OfferRuleWorkflowFinanceData): void
{
  const activeDownValues = Number(finance.moneyDown !== null) + Number(finance.percentageDown !== null);
  if (finance.mode === 'custom'
    && (finance.paymentAmount === null || finance.apr === null || finance.term === null || activeDownValues !== 1)
  ) {
    throw new RangeError('Custom finance values are incomplete or contradictory.');
  }
}

function requireGeneratedDraft(draft: EnhancedRuleDraft): GeneratedOfferSetDraft
{
  if (draft.generatedOfferSet === null) {
    throw new RangeError('The complete generated offer set is required.');
  }

  return draft.generatedOfferSet;
}

function requireServerMetadata(draft: EnhancedRuleDraft): EnhancedRuleServerMetadata
{
  const metadata = draft.serverMetadata;
  if (metadata === null || !Number.isSafeInteger(metadata.ruleId) || metadata.ruleId <= 0 || metadata.version.trim() === '') {
    throw new RangeError('Authoritative rule identity and version are required.');
  }

  return metadata;
}

function requirePositiveSafeInteger(value: unknown, name: string): number
{
  if (typeof value !== 'number' || !Number.isSafeInteger(value) || value <= 0) {
    throw new RangeError(`${name} must be a positive safe integer.`);
  }

  return value;
}

function normalizeOptionalSafeInteger(
  value: unknown,
  positive: boolean,
  name: string
): number | null
{
  let normalized: number | null = null;
  if (value !== null && value !== undefined && value !== '') {
    if (typeof value !== 'number' || !Number.isSafeInteger(value) || value < 0 || (positive && value === 0)) {
      throw new RangeError(`${name} is invalid.`);
    }
    normalized = value;
  }

  return normalized;
}

function normalizeRequiredDecimal(value: unknown, maximum: string, positive: boolean): string
{
  const normalized = normalizeOptionalDecimal(value, maximum);
  if (normalized === null || (positive && normalized === '0.00')) {
    throw new RangeError('A required financial value is invalid.');
  }

  return normalized;
}

function normalizeOptionalDecimal(value: unknown, maximum: string): string | null
{
  let normalized: string | null = null;
  if (value !== null && value !== undefined && value !== '') {
    let text = String(value).trim();
    if (typeof value === 'number') {
      text = value.toString();
    }
    if (!/^(0|[1-9]\d*)(\.\d{1,2})?$/.test(text)) {
      throw new RangeError('Financial values allow at most two decimal places.');
    }
    const parts = text.split('.');
    const decimalPart = (parts[1] ?? '').padEnd(2, '0');
    normalized = `${parts[0]}.${decimalPart}`;
    if (!decimalPattern.test(normalized) || compareDecimal(normalized, maximum) > 0) {
      throw new RangeError('A financial value exceeds the supported range.');
    }
  }

  return normalized;
}

function compareDecimal(left: string, right: string): number
{
  let comparison = left.length - right.length;
  if (comparison === 0) {
    comparison = left.localeCompare(right);
  }

  return comparison;
}

function normalizeOptionalText(value: unknown): string | null
{
  let normalized: string | null = null;
  if (typeof value === 'string' && value.trim() !== '') {
    if (value.length > maximumTextLength) {
      throw new RangeError('Text values may not exceed 255 characters.');
    }
    normalized = value;
  }

  return normalized;
}

function normalizeVehicleCondition(value: unknown): 'New' | 'Used' | 'CPO' | 'Any'
{
  const allowed = ['New', 'Used', 'CPO', 'Any'];
  if (typeof value !== 'string' || !allowed.includes(value)) {
    throw new RangeError('Vehicle condition is invalid.');
  }

  return value as 'New' | 'Used' | 'CPO' | 'Any';
}

function normalizeRuleStatus(value: unknown): 'Active' | 'Inactive'
{
  if (value !== 'Active' && value !== 'Inactive') {
    throw new RangeError('Rule status is invalid.');
  }

  return value;
}

function buildComparableDraft(draft: EnhancedRuleDraft): object
{
  let generatedOfferSet: OfferRuleWorkflowOffersData | null = null;
  if (draft.generatedOfferSet !== null) {
    generatedOfferSet = mapGeneratedOffers(draft.generatedOfferSet);
  }
  const comparable = {
    ruleType: draft.ruleType,
    rule: mapDraftRuleData(draft),
    generatedOfferSet,
    visibility: draft.generatedOfferSet?.multiVinVisibility ?? null
  };

  return comparable;
}

export function parseOfferRuleWorkflowAggregateCollection(value: unknown): OfferRuleWorkflowAggregateCollection
{
  if (!isAggregateCollection(value)) {
    throw new TypeError('The aggregate response is invalid.');
  }

  return value;
}

export function parseOfferRuleWorkflowAcceptance(value: unknown): OfferRuleWorkflowAcceptance
{
  if (!isAcceptance(value)) {
    throw new TypeError('The workflow acceptance response is invalid.');
  }

  return value;
}

export function parseOfferRuleWorkflowCommandStatus(value: unknown): OfferRuleWorkflowCommandStatusResponse
{
  if (!isCommandStatus(value)) {
    throw new TypeError('The workflow command status response is invalid.');
  }

  return value;
}

function isAggregateCollection(value: unknown): value is OfferRuleWorkflowAggregateCollection
{
  let valid = false;
  if (isRecord(value) && hasOnlyKeys(value, ['schemaVersion', 'accountId', 'items'])) {
    const items = value['items'];
    valid = value['schemaVersion'] === offerRuleWorkflowSchemaVersion
      && isPositiveSafeInteger(value['accountId'])
      && Array.isArray(items)
      && items.every((item) => isAggregateItem(item))
      && hasUniqueItemIds(items);
  }

  return valid;
}

function isAggregateItem(value: unknown): value is OfferRuleWorkflowAggregateItem
{
  let valid = false;
  const legacyKeys = ['ruleId', 'type', 'version', 'rule', 'offerSet'];
  const currentKeys = ['ruleId', 'type', 'version', 'created', 'modified', 'rule', 'offerSet'];
  const validShape = isRecord(value)
    && (hasOnlyKeys(value, legacyKeys) || hasOnlyKeys(value, currentKeys));
  if (validShape && isRecord(value)) {
    const type = value['type'];
    const baseValid = isPositiveSafeInteger(value['ruleId'])
      && isNonEmptyString(value['version'])
      && isOptionalTimestamp(value['created'] ?? null)
      && isOptionalTimestamp(value['modified'] ?? null)
      && isRuleData(value['rule'], type);
    if (type === offerSetType) {
      valid = baseValid && isPersistedOfferSet(value['offerSet']);
    }
    if (type === dollarDiscountType) {
      valid = baseValid && value['offerSet'] === null;
    }
  }

  return valid;
}

function isRuleData(value: unknown, type: unknown): value is OfferRuleWorkflowRuleData
{
  let valid = false;
  if (isRecord(value) && hasValidRuleKeys(value)) {
    valid = isOptionalPositiveSafeInteger(value['year'])
      && isOptionalText(value['make'])
      && isOptionalText(value['model'])
      && isOptionalText(value['trim'])
      && isDecimal(value['maxSalePrice'], maximumCurrency, false)
      && isOptionalNonNegativeSafeInteger(value['vehicleLimit'])
      && isVehicleCondition(value['vehicleCondition'])
      && isRuleStatus(value['status'])
      && isPrivacy(value['privacy']);
    valid = valid && isRuleDiscriminatorValid(value, type);
  }

  return valid;
}

function hasValidRuleKeys(value: Record<string, unknown>): boolean
{
  const keys = [
    'year', 'make', 'model', 'trim', 'maxSalePrice', 'vehicleLimit',
    'vehicleCondition', 'dollarAmountDiscount', 'status', 'privacy'
  ];
  const valid = hasOnlyKeys(value, keys);

  return valid;
}

function isRuleDiscriminatorValid(value: Record<string, unknown>, type: unknown): boolean
{
  let valid = false;
  if (type === offerSetType) {
    valid = isPositiveSafeInteger(value['year'])
      && isNonEmptyText(value['make'])
      && isNonEmptyText(value['model'])
      && value['dollarAmountDiscount'] === null;
  }
  if (type === dollarDiscountType) {
    valid = value['privacy'] === 'private'
      && isDecimal(value['dollarAmountDiscount'], maximumCurrency, true);
  }

  return valid;
}

function isPersistedOfferSet(value: unknown): boolean
{
  let valid = false;
  const keys = ['sourceOfferSetId', 'generatedOfferSetId', 'visibilityScope', 'offers'];
  if (isRecord(value) && hasOnlyKeys(value, keys)) {
    valid = value['sourceOfferSetId'] === null
      && isPositiveSafeInteger(value['generatedOfferSetId'])
      && isVisibility(value['visibilityScope'])
      && isOffers(value['offers']);
  }

  return valid;
}

function isOffers(value: unknown): value is OfferRuleWorkflowOffersData
{
  let valid = false;
  if (isRecord(value) && hasOnlyKeys(value, ['lease', 'alternateLease', 'finance', 'promotionalApr'])) {
    valid = isLease(value['lease'])
      && isLease(value['alternateLease'])
      && isFinance(value['finance'])
      && isPromotionalApr(value['promotionalApr']);
  }

  return valid;
}

function isLease(value: unknown): value is OfferRuleWorkflowLeaseData
{
  let valid = false;
  const keys = ['mode', 'paymentAmount', 'moneyDown', 'percentageDown', 'term', 'annualMileage', 'additionalText'];
  if (isRecord(value) && hasOnlyKeys(value, keys)) {
    valid = isSectionMode(value['mode'])
      && isNullableDecimal(value['paymentAmount'], maximumCurrency)
      && isNullableDecimal(value['moneyDown'], maximumCurrency)
      && isNullableDecimal(value['percentageDown'], maximumPercentage)
      && isOptionalPositiveSafeInteger(value['term'])
      && isOptionalNonNegativeSafeInteger(value['annualMileage'])
      && isOptionalText(value['additionalText'])
      && hasValidCustomLeaseShape(value);
  }

  return valid;
}

function isFinance(value: unknown): value is OfferRuleWorkflowFinanceData
{
  let valid = false;
  const keys = ['mode', 'paymentAmount', 'moneyDown', 'percentageDown', 'apr', 'term', 'additionalText'];
  if (isRecord(value) && hasOnlyKeys(value, keys)) {
    valid = isSectionMode(value['mode'])
      && isNullableDecimal(value['paymentAmount'], maximumCurrency)
      && isNullableDecimal(value['moneyDown'], maximumCurrency)
      && isNullableDecimal(value['percentageDown'], maximumPercentage)
      && isNullableDecimal(value['apr'], maximumPercentage)
      && isOptionalPositiveSafeInteger(value['term'])
      && isOptionalText(value['additionalText'])
      && hasValidCustomFinanceShape(value);
  }

  return valid;
}

function isPromotionalApr(value: unknown): value is OfferRuleWorkflowPromotionalAprData
{
  let valid = false;
  const keys = ['mode', 'apr', 'term', 'additionalText'];
  if (isRecord(value) && hasOnlyKeys(value, keys)) {
    valid = isSectionMode(value['mode'])
      && isNullableDecimal(value['apr'], maximumPercentage)
      && isOptionalPositiveSafeInteger(value['term'])
      && isOptionalText(value['additionalText']);
    if (value['mode'] === 'custom') {
      valid = valid && value['apr'] !== null && value['term'] !== null;
    }
  }

  return valid;
}

function hasValidCustomLeaseShape(value: Record<string, unknown>): boolean
{
  let valid = true;
  if (value['mode'] === 'custom') {
    const downCount = Number(value['moneyDown'] !== null) + Number(value['percentageDown'] !== null);
    valid = value['paymentAmount'] !== null
      && value['term'] !== null
      && value['annualMileage'] !== null
      && downCount === 1;
  }

  return valid;
}

function hasValidCustomFinanceShape(value: Record<string, unknown>): boolean
{
  let valid = true;
  if (value['mode'] === 'custom') {
    const downCount = Number(value['moneyDown'] !== null) + Number(value['percentageDown'] !== null);
    valid = value['paymentAmount'] !== null
      && value['apr'] !== null
      && value['term'] !== null
      && downCount === 1;
  }

  return valid;
}

function isAcceptance(value: unknown): value is OfferRuleWorkflowAcceptance
{
  let valid = false;
  const keys = ['schemaVersion', 'commandId', 'requestId', 'status', 'phase', 'statusUri'];
  if (isRecord(value) && hasOnlyKeys(value, keys)) {
    valid = value['schemaVersion'] === offerRuleWorkflowSchemaVersion
      && isPositiveSafeInteger(value['commandId'])
      && isRequestId(value['requestId'])
      && value['status'] === 'queued'
      && value['phase'] === 'queued'
      && isNonEmptyString(value['statusUri']);
  }

  return valid;
}

function isCommandStatus(value: unknown): value is OfferRuleWorkflowCommandStatusResponse
{
  let valid = false;
  const keys = [
    'schemaVersion', 'commandId', 'requestId', 'accountId', 'status', 'phase', 'attempt',
    'retryable', 'counts', 'createdAt', 'startedAt', 'completedAt', 'checkpoint', 'created',
    'deleted', 'items', 'authoritativeItemIds', 'error'
  ];
  if (isRecord(value) && hasOnlyKeys(value, keys)) {
    valid = isCommandIdentity(value)
      && isCommandLifecycle(value)
      && isCounts(value['counts'])
      && isOptionalTimestamp(value['createdAt'])
      && isOptionalTimestamp(value['startedAt'])
      && isOptionalTimestamp(value['completedAt'])
      && isOptionalCheckpoint(value['checkpoint'])
      && isCreatedMappings(value['created'])
      && isDeletedMappings(value['deleted'])
      && isAggregateItems(value['items'])
      && isPositiveIdArray(value['authoritativeItemIds'])
      && isOptionalControlledError(value['error']);
  }

  return valid;
}

function isCommandIdentity(value: Record<string, unknown>): boolean
{
  const valid = value['schemaVersion'] === offerRuleWorkflowSchemaVersion
    && isPositiveSafeInteger(value['commandId'])
    && isRequestId(value['requestId'])
    && isPositiveSafeInteger(value['accountId']);

  return valid;
}

function isCommandLifecycle(value: Record<string, unknown>): boolean
{
  let valid = typeof value['retryable'] === 'boolean'
    && (value['attempt'] === null || isPositiveSafeInteger(value['attempt']));
  const status = value['status'];
  const phase = value['phase'];
  if (status === 'queued') {
    valid = valid && phase === 'queued';
  }
  if (status === 'processing') {
    valid = valid && ['persisting', 'syncing_disclosures', 'processing'].includes(String(phase));
  }
  if (status === 'completed') {
    valid = valid && phase === 'completed';
  }
  if (status === 'failed') {
    valid = valid && phase === 'failed';
  }
  valid = valid && ['queued', 'processing', 'completed', 'failed'].includes(String(status));

  return valid;
}

function isCounts(value: unknown): boolean
{
  let valid = false;
  if (isRecord(value) && hasOnlyKeys(value, ['created', 'updated', 'deleted'])) {
    valid = isNonNegativeSafeInteger(value['created'])
      && isNonNegativeSafeInteger(value['updated'])
      && isNonNegativeSafeInteger(value['deleted']);
  }

  return valid;
}

function isOptionalCheckpoint(value: unknown): boolean
{
  let valid = value === null;
  const keys = [
    'failedPhase', 'persistenceCommitted', 'ruleIds', 'generatedOfferSetIds', 'versions',
    'disclosureTargetIds', 'disclosureProcessedIds', 'processingTargetIds', 'processingResultIds'
  ];
  if (isRecord(value) && hasOnlyKeys(value, keys)) {
    valid = isFailedPhase(value['failedPhase'])
      && typeof value['persistenceCommitted'] === 'boolean'
      && isPositiveIdArray(value['ruleIds'])
      && isPositiveIdArray(value['generatedOfferSetIds'])
      && isVersionRecord(value['versions'])
      && isPositiveIdArray(value['disclosureTargetIds'])
      && isPositiveIdArray(value['disclosureProcessedIds'])
      && isPositiveIdArray(value['processingTargetIds'])
      && isPositiveIdArray(value['processingResultIds']);
  }

  return valid;
}

function isCreatedMappings(value: unknown): boolean
{
  let valid = false;
  if (Array.isArray(value)) {
    valid = value.every((mapping) => isCreatedMapping(mapping));
  }

  return valid;
}

function isCreatedMapping(value: unknown): boolean
{
  let valid = false;
  if (isRecord(value) && hasOnlyKeys(value, ['clientKey', 'ruleId', 'generatedOfferSetId', 'version'])) {
    valid = isNonEmptyString(value['clientKey'])
      && isPositiveSafeInteger(value['ruleId'])
      && (value['generatedOfferSetId'] === null || isPositiveSafeInteger(value['generatedOfferSetId']))
      && isNonEmptyString(value['version']);
  }

  return valid;
}

function isDeletedMappings(value: unknown): boolean
{
  let valid = false;
  if (Array.isArray(value)) {
    valid = value.every((mapping) => isDeletedMapping(mapping));
  }

  return valid;
}

function isDeletedMapping(value: unknown): boolean
{
  let valid = false;
  if (isRecord(value) && hasOnlyKeys(value, ['ruleId', 'generatedOfferSetId'])) {
    valid = isPositiveSafeInteger(value['ruleId'])
      && (value['generatedOfferSetId'] === null || isPositiveSafeInteger(value['generatedOfferSetId']));
  }

  return valid;
}

function isOptionalControlledError(value: unknown): boolean
{
  let valid = value === null;
  if (isRecord(value) && hasRequiredAndOptionalKeys(value, ['code', 'message'], ['operation'])) {
    const codes = [
      'invalid_command_parameters', 'not_found', 'validation_failed', 'aggregate_version_conflict',
      'aggregate_integrity_conflict', 'persistence_failed', 'publication_failed',
      'disclosure_sync_failed_manual_intervention', 'processing_failed_manual_intervention',
      'worker_terminated'
    ];
    valid = codes.includes(String(value['code']))
      && isNonEmptyString(value['message'])
      && isOptionalErrorOperation(value['operation']);
  }

  return valid;
}

function isOptionalErrorOperation(value: unknown): boolean
{
  let valid = value === undefined || value === null;
  if (isRecord(value)) {
    const kind = value['kind'];
    valid = hasRequiredAndOptionalKeys(value, ['kind'], ['clientKey', 'ruleId'])
      && ['create', 'update', 'delete'].includes(String(kind))
      && (value['clientKey'] === undefined || isNonEmptyString(value['clientKey']))
      && (value['ruleId'] === undefined || isPositiveSafeInteger(value['ruleId']));
  }

  return valid;
}

function isAggregateItems(value: unknown): boolean
{
  let valid = false;
  if (Array.isArray(value)) {
    valid = value.every((item) => isAggregateItem(item)) && hasUniqueItemIds(value);
  }

  return valid;
}

function hasUniqueItemIds(items: readonly unknown[]): boolean
{
  const ids = items.map((item) => {
    let id: unknown = null;
    if (isRecord(item)) {
      id = item['ruleId'];
    }

    return id;
  });
  const uniqueIds = new Set(ids);

  return uniqueIds.size === ids.length;
}

function isVersionRecord(value: unknown): boolean
{
  let valid = false;
  if (isRecord(value)) {
    valid = Object.entries(value).every(([ruleId, version]) => {
      const numericRuleId = Number(ruleId);

      return isPositiveSafeInteger(numericRuleId) && isNonEmptyString(version);
    });
  }

  return valid;
}

function isPositiveIdArray(value: unknown): boolean
{
  let valid = false;
  if (Array.isArray(value)) {
    const ids = value.filter((id) => isPositiveSafeInteger(id));
    valid = ids.length === value.length && new Set(ids).size === ids.length;
  }

  return valid;
}

function isDecimal(value: unknown, maximum: string, positive: boolean): boolean
{
  let valid = typeof value === 'string'
    && decimalPattern.test(value)
    && compareDecimal(value, maximum) <= 0;
  if (positive) {
    valid = valid && value !== '0.00';
  }

  return valid;
}

function isNullableDecimal(value: unknown, maximum: string): boolean
{
  const valid = value === null || isDecimal(value, maximum, false);

  return valid;
}

function isRecord(value: unknown): value is Record<string, unknown>
{
  const valid = typeof value === 'object' && value !== null && !Array.isArray(value);

  return valid;
}

function hasOnlyKeys(value: Record<string, unknown>, allowedKeys: readonly string[]): boolean
{
  const keys = Object.keys(value);
  const valid = keys.length === allowedKeys.length && keys.every((key) => allowedKeys.includes(key));

  return valid;
}

function hasRequiredAndOptionalKeys(
  value: Record<string, unknown>,
  requiredKeys: readonly string[],
  optionalKeys: readonly string[]
): boolean
{
  const keys = Object.keys(value);
  const allowedKeys = [...requiredKeys, ...optionalKeys];
  const valid = requiredKeys.every((key) => keys.includes(key))
    && keys.every((key) => allowedKeys.includes(key));

  return valid;
}

function isPositiveSafeInteger(value: unknown): value is number
{
  const valid = typeof value === 'number' && Number.isSafeInteger(value) && value > 0;

  return valid;
}

function isNonNegativeSafeInteger(value: unknown): value is number
{
  const valid = typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;

  return valid;
}

function isOptionalPositiveSafeInteger(value: unknown): boolean
{
  const valid = value === null || isPositiveSafeInteger(value);

  return valid;
}

function isOptionalNonNegativeSafeInteger(value: unknown): boolean
{
  const valid = value === null || isNonNegativeSafeInteger(value);

  return valid;
}

function isNonEmptyString(value: unknown): value is string
{
  const valid = typeof value === 'string' && value.trim() !== '';

  return valid;
}

function isNonEmptyText(value: unknown): boolean
{
  const valid = isNonEmptyString(value) && value.length <= maximumTextLength;

  return valid;
}

function isOptionalText(value: unknown): boolean
{
  const valid = value === null || isNonEmptyText(value);

  return valid;
}

function isRequestId(value: unknown): value is string
{
  const valid = typeof value === 'string' && requestIdPattern.test(value);

  return valid;
}

function isVehicleCondition(value: unknown): boolean
{
  const valid = ['New', 'Used', 'CPO', 'Any'].includes(String(value));

  return valid;
}

function isRuleStatus(value: unknown): boolean
{
  const valid = value === 'Active' || value === 'Inactive';

  return valid;
}

function isPrivacy(value: unknown): boolean
{
  const valid = value === 'public' || value === 'private';

  return valid;
}

function isVisibility(value: unknown): boolean
{
  const valid = value === 'platform_full' || value === 'inventory_only';

  return valid;
}

function isSectionMode(value: unknown): boolean
{
  const valid = value === 'custom' || value === 'default';

  return valid;
}

function isFailedPhase(value: unknown): boolean
{
  const valid = value === null
    || ['queued', 'persisting', 'syncing_disclosures', 'processing'].includes(String(value));

  return valid;
}

function isOptionalTimestamp(value: unknown): boolean
{
  let valid = value === null;
  if (typeof value === 'string' && value.trim() !== '') {
    valid = Number.isFinite(Date.parse(value));
  }

  return valid;
}
